import { useEffect } from "react";
import styled from "styled-components";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { I18n } from "aws-amplify";
import { translations } from "@aws-amplify/ui";
import { useNavigate } from "react-router-dom";
import { ensurePrivateAssetAccess } from "./Utils";
import { LoginModalStore } from "./Store";

I18n.putVocabularies(translations);
I18n.putVocabulariesForLanguage("he", {
  "Sign In": "התחברות",
  "Sign in": "התחברות",
  "Sign Up": "הרשמה",
  "Create Account": "יצירת חשבון",
  Username: "שם משתמש",
  Password: "סיסמה",
  Email: "אימייל",
  "Phone Number": "מספר טלפון",
  Code: "קוד",
  Confirm: "אישור",
  Submit: "שליחה",
  "Enter your Username": "שם משתמש",
  "Enter your username": "שם משתמש",
  "Enter your Password": "הכניסו סיסמה",
  "Forgot your password?": "שכחת סיסמה\u200f?",
  "Reset Password": "איפוס סיסמה",
  "Back to Sign In": "חזרה להתחברות",
  "Send code": "שליחת קוד",
  "No account?": "אין חשבון?",
  "Create Account ": "יצירת חשבון",
  "Back to Sign In ": "חזרה להתחברות",
  "Please confirm your Password": "נא לאשר את הסיסמה",
});
I18n.setLanguage("he");

export function Login() {
  const { route, user } = useAuthenticator((context) => [
    context.route,
    context.user,
  ]);
  const { loginModal, closeLoginModal } = LoginModalStore();
  const navigate = useNavigate();
  const isModal = loginModal.isOpen;

  useEffect(() => {
    if (route !== "authenticated" || !user || !loginModal.isOpen) return;

    closeLoginModal();

    ensurePrivateAssetAccess({
      user,
    }).catch((err) => {
      console.error("Failed to ensure private asset access:", err);
    });

    navigate("/welcome", { replace: true });
  }, [route, user, loginModal, closeLoginModal, navigate]);

  if (!loginModal.isOpen) return null;

  return (
    <Page $isModal={isModal}>
      <Backdrop
        $isModal={isModal}
        onClick={() => {
          if (isModal) {
            closeLoginModal();
          }
        }}
      >
        <Modal dir="rtl" onClick={(event) => event.stopPropagation()}>
          {isModal ? (
            <CloseButton
              type="button"
              onClick={() => {
                closeLoginModal();
              }}
            >
              x
            </CloseButton>
          ) : null}
          <Notice>
            {loginModal.notice || "יש להתחבר על מנת לצפות במוצגים פרטיים"}
          </Notice>
          <Authenticator initialState="signIn" hideSignUp={true} />
        </Modal>
      </Backdrop>
    </Page>
  );
}

const loginTheme = {
  colors: {
    pageStart: "#e7f6ff",
    pageMid: "#f6fbff",
    pageEnd: "#ffffff",
    overlayModal: "rgba(11, 36, 58, 0.28)",
    overlayPage: "rgba(11, 36, 58, 0.18)",
    surface: "#e7f6ff",
    border: "#d0e4f3",
    borderSoft: "#cde1ef",
    textPrimary: "#0c2b49",
    textField: "#22313d",
    placeholder: "#6b7e8d",
    inputBg: "#f7fbfe",
    button: "#0c2b49",
    buttonHover: "#134062",
  },
  radii: {
    modal: "14px",
    tabs: "6px",
    control: "8px",
  },
  shadows: {
    modal: "0 12px 28px rgba(19, 64, 92, 0.18)",
  },
};

const Page = styled.div`
  min-height: 100dvh;
  background: linear-gradient(
    180deg,
    ${loginTheme.colors.pageStart} 0%,
    ${loginTheme.colors.pageMid} 45%,
    ${loginTheme.colors.pageEnd} 100%
  );
`;

const Backdrop = styled.main`
  min-height: 100dvh;
  background: ${(props) =>
    props.$isModal ? loginTheme.colors.overlayModal : loginTheme.colors.overlayPage};
  backdrop-filter: ${(props) => (props.$isModal ? "blur(2px)" : "none")};
  -webkit-backdrop-filter: ${(props) => (props.$isModal ? "blur(2px)" : "none")};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 10px;
  position: ${(props) => (props.$isModal ? "fixed" : "static")};
  inset: ${(props) => (props.$isModal ? "0" : "auto")};
  z-index: ${(props) => (props.$isModal ? "1200" : "auto")};
`;

const Modal = styled.section`
  --auth-field-gap: 4px;
  --signin-spacer: 12px;
  position: relative;
  width: 100%;
  max-width: 400px;
  box-sizing: border-box;
  background: ${loginTheme.colors.surface};
  border: 1px solid ${loginTheme.colors.border};
  border-radius: ${loginTheme.radii.modal};
  box-shadow: ${loginTheme.shadows.modal};
  padding: 8px 8px 6px;
  max-height: calc(100dvh - 100px);
  overflow-y: auto;
  direction: rtl;

  .amplify-authenticator {
    direction: rtl;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    --amplify-components-flex-gap: 4px;
    --amplify-components-field-gap: 0;
    --amplify-components-tabs-item-active-border-color: ${loginTheme.colors.textPrimary};
    --amplify-components-tabs-item-active-color: ${loginTheme.colors.textPrimary};
    --amplify-components-authenticator-router-box-shadow: none;
    --amplify-components-authenticator-router-border-width: 0;
    --amplify-components-fieldcontrol-background-color: ${loginTheme.colors.inputBg};
    --amplify-components-fieldcontrol-border-color: #b8d2e5;
    --amplify-components-fieldcontrol-border-width: 1px;
    --amplify-components-fieldcontrol-color: ${loginTheme.colors.textField};
    --amplify-components-fieldcontrol-focus-border-color: ${loginTheme.colors.textPrimary};
    --amplify-components-fieldcontrol-focus-box-shadow: 0 0 0 1px ${loginTheme.colors.textPrimary};
    --amplify-components-button-primary-background-color: ${loginTheme.colors.button};
    --amplify-components-button-primary-hover-background-color: ${loginTheme.colors.buttonHover};
    --amplify-components-button-primary-color: #fff;
    --amplify-components-button-primary-border-radius: ${loginTheme.radii.control};
    --amplify-components-button-link-color: ${loginTheme.colors.textPrimary};
    --amplify-components-button-link-hover-color: ${loginTheme.colors.buttonHover};
  }

  [data-amplify-authenticator] [data-amplify-container] {
    display: block;
    width: 100%;
    max-width: 100%;
  }

  [data-amplify-authenticator] [data-amplify-router] {
    border: 0;
    box-shadow: none;
    background: transparent;
    padding: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  [data-amplify-authenticator] [data-amplify-router-content] {
    overflow-y: auto;
    scrollbar-gutter: stable;
    padding-inline-end: 2px;
  }

  [data-amplify-authenticator] [role="tabpanel"] {
    animation: none;
    transition: none;
  }

  [data-amplify-authenticator] [data-amplify-router-content],
  [data-amplify-authenticator] form,
  [data-amplify-authenticator] fieldset,
  [data-amplify-authenticator] .amplify-field,
  [data-amplify-authenticator] .amplify-flex {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  [data-amplify-authenticator] form {
    margin: 0;
    gap: 0;
    row-gap: 0;
  }

  [data-amplify-authenticator] fieldset {
    margin: 0;
    padding: 0;
    border: 0;
    min-inline-size: 0;
    gap: 0;
    row-gap: 0;
  }

  [data-amplify-authenticator] fieldset > .amplify-flex {
    gap: var(--auth-field-gap);
  }

  .amplify-tabs {
    display: none;
  }

  .amplify-tabs-item {
    flex: 1 1 0;
    min-width: 0;
    border-radius: ${loginTheme.radii.tabs};
    color: ${loginTheme.colors.textPrimary};
    font-weight: 500;
    font-size: 13px;
    padding-inline: 8px;
    min-height: 38px;
  }

  .amplify-tabs-item[data-state="active"] {
    background: ${loginTheme.colors.textPrimary};
    color: #fff;
  }

  .amplify-field,
  .amplify-textfield,
  .amplify-passwordfield {
    margin: 0;
  }

  .amplify-label {
    margin-bottom: 2px;
    font-size: 14px;
    line-height: 1.3;
  }

  [data-amplify-authenticator-signin] .amplify-label,
  [data-amplify-authenticator-signup] .amplify-label {
    font-size: 14px;
    line-height: 1.3;
  }

  .amplify-input {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    background: ${loginTheme.colors.inputBg};
    border: 1px solid #b8d2e5;
    color: ${loginTheme.colors.textField};
    min-height: 36px;
    padding-block: 6px;
    font-size: 14px;
  }

  [data-amplify-authenticator-signin] .amplify-input,
  [data-amplify-authenticator-signup] .amplify-input {
    min-height: 36px;
    font-size: 14px;
    padding-block: 6px;
  }

  .amplify-input::placeholder {
    color: ${loginTheme.colors.placeholder};
  }

  .amplify-button--primary {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    background: ${loginTheme.colors.button};
    border-color: ${loginTheme.colors.button};
    min-height: 36px;
    font-size: 14px;
    font-weight: 500;
    margin-top: 8px;
  }

  .amplify-button--primary:hover {
    background: ${loginTheme.colors.buttonHover};
    border-color: ${loginTheme.colors.buttonHover};
  }

  .amplify-button--link {
    color: ${loginTheme.colors.textPrimary};
    text-decoration: none;
    direction: rtl;
    unicode-bidi: plaintext;
    margin: 0;
    padding-top: 0;
    padding-bottom: 0;
    font-size: 13px;
  }

  [data-amplify-router-content] {
    padding-top: 0;
  }

  [data-amplify-footer] {
    margin-top: 4px;
    padding-top: 0;
    padding-bottom: 0;
  }

  [data-amplify-authenticator-signin] fieldset {
    margin-bottom: var(--signin-spacer);
  }

  [data-amplify-authenticator-signin] [data-amplify-footer] {
    margin-top: 4px;
  }

  .amplify-label,
  .amplify-input,
  .amplify-button,
  .amplify-heading,
  .amplify-text {
    font-family: Rubik;
    text-align: right;
  }

  .amplify-field-group__control {
    border-radius: ${loginTheme.radii.control};
  }

  .amplify-input:focus,
  .amplify-button:focus-visible {
    outline: 2px solid ${loginTheme.colors.buttonHover};
    outline-offset: 1px;
  }

  .amplify-field-group {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .amplify-field-group__field-wrapper {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
  }

  .amplify-field-group__outer-end {
    flex: 0 0 auto;
  }

  .amplify-field__show-password {
    width: auto;
    min-width: 42px;
    padding-inline: 10px;
  }
`;

const CloseButton = styled.button`
  border: 0;
  background: transparent;
  color: ${loginTheme.colors.textPrimary};
  font-family: Rubik;
  font-size: 22px;
  line-height: 1;
  font-weight: 400;
  cursor: pointer;
  position: absolute;
  top: 6px;
  right: 8px;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 8px;
  display: grid;
  place-items: center;

  &:hover {
    background: rgba(12, 43, 73, 0.08);
  }
`;

const Notice = styled.p`
  margin: 4px 0 6px;
  font-family: Rubik;
  font-size: 13px;
  line-height: 1.35;
  font-weight: 400;
  color: ${loginTheme.colors.textPrimary};
  text-align: center;
`;
